import { useEffect, useId, useRef, useState } from "react";

import {
  attachDesktopSshPasswordPrompts,
  resolveSshPasswordPrompt,
  subscribeSshPasswordPrompts,
  type SshPasswordPromptRequest,
} from "../connection/sshPasswordPrompt";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";

function formatRemaining(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

/**
 * Asks for an SSH password when a connection needs one this device has not saved.
 *
 * Ported from T3 Code. One prompt at a time, oldest first: two dialogs stacked over each other
 * cannot be told apart, and the person would not know which destination they were answering.
 */
export function SshPasswordPromptDialog() {
  const [requests, setRequests] = useState<ReadonlyArray<SshPasswordPromptRequest>>([]);
  useEffect(() => subscribeSshPasswordPrompts(setRequests), []);
  // The main process raises a prompt while it is opening a forward, and nothing would show it
  // without a listener on the bridge. Attached here because this dialog is what answers.
  useEffect(() => attachDesktopSshPasswordPrompts(), []);
  const current = requests[0] ?? null;
  if (current === null) return null;
  return <ActiveSshPasswordPrompt key={current.requestId} request={current} />;
}

function ActiveSshPasswordPrompt({ request }: { readonly request: SshPasswordPromptRequest }) {
  const [password, setPassword] = useState("");
  const [now, setNow] = useState(() => Date.now());
  const inputRef = useRef<HTMLInputElement | null>(null);
  const formId = useId();

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
    return () => window.cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(interval);
  }, []);

  const remainingMs = Math.max(0, request.expiresAtMs - now);
  const expired = remainingMs <= 0;
  const remainingLabel = formatRemaining(Math.ceil(remainingMs / 1_000));

  const cancel = () => resolveSshPasswordPrompt(request.requestId, null);

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) cancel();
      }}
    >
      <DialogPopup className="max-w-md" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>SSH password required</DialogTitle>
          <DialogDescription>
            AFAR needs your SSH password to connect to <code>{request.destination}</code>. It is
            handed to the local SSH process for this connection attempt and is not saved.
          </DialogDescription>
        </DialogHeader>
        <DialogPanel className="space-y-3" scrollFade={false}>
          <form
            className="space-y-3"
            id={formId}
            onSubmit={(event) => {
              event.preventDefault();
              if (!expired) resolveSshPasswordPrompt(request.requestId, password);
            }}
          >
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-foreground">{request.prompt}</p>
                <span
                  className={
                    expired
                      ? "shrink-0 text-xs font-medium text-destructive"
                      : "shrink-0 text-xs text-muted-foreground"
                  }
                >
                  {expired ? "Expired" : remainingLabel}
                </span>
              </div>
              <Input
                ref={inputRef}
                autoComplete="current-password"
                aria-label="SSH password"
                disabled={expired}
                name="ssh-password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>
            <p className="text-sm text-muted-foreground">
              {expired
                ? "This prompt expired. Try connecting again."
                : "Set up key authentication for this destination to avoid being asked again."}
            </p>
          </form>
        </DialogPanel>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={cancel}>
            {expired ? "Dismiss" : "Cancel"}
          </Button>
          <Button disabled={expired} form={formId} type="submit">
            Continue
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
